<template>
  <CatalogoPagina
    titulo="Marcas"
    prefijo="marcas"
    :resumen="resumen"
    :columnas="columnas"
    :total="total"
    :pagina="paginaActual"
    :total-paginas="totalPaginas"
    :limite="limite"
    :cargando="cargando"
    :ya-cargo="yaCargo"
    :error="errorCarga"
    mensaje-error="No pudimos cargar las marcas."
    :orden-campo="ordenCampo"
    :orden-asc="ordenAsc"
    :aviso="aviso"
    @limite="alCambiarLimite"
    @ordenar="ordenarPor"
    @pagina="cambiarPagina"
    @reintentar="initialize"
    @cerrar-aviso="aviso = ''"
  >
    <template #acciones>
      <button type="button" class="rm-btn rm-btn-primario" data-testid="marcas-nueva" @click="nuevaMarca">
        <AppIcon name="plus" size="h-4 w-4" />
        Nueva marca
      </button>
    </template>

    <template #filtros>
      <!-- El tipo no se captura: lo decide la pestaña (autos para modelos y
           vehículos, refacciones para productos) -->
      <div class="mc-pestanas" role="tablist" aria-label="Tipo de marca">
        <button
          v-for="pestana in pestanas"
          :key="pestana.tipo"
          type="button"
          role="tab"
          class="mc-pestana"
          :class="{ activa: tipo === pestana.tipo }"
          :aria-selected="tipo === pestana.tipo ? 'true' : 'false'"
          :data-testid="'marcas-pestana-' + pestana.clave"
          @click="cambiarTipo(pestana.tipo)"
        >
          {{ pestana.label }}
          <span class="mc-cuenta">{{ totales[pestana.clave] }}</span>
        </button>
      </div>

      <AppBusqueda v-model="busqueda" placeholder="Buscar marcas" testid="marcas-buscar" @buscar="aplicarFiltros" />
    </template>

    <template #filas>
      <tr v-for="marca in marcas" :key="marca._id" class="mc-fila" data-testid="marca-fila">
        <td class="mc-celda-marca">
          <div class="cat-nombre">
            <MarcaLogo :nombre="marca.name" />
            <span class="cat-nombre-texto">{{ nombreDe(marca) }}</span>
          </div>
        </td>
        <td class="mc-celda-conteo">
          <!-- Autos: la cifra lleva a Modelos filtrado por esta marca -->
          <router-link
            v-if="marca.uso && esAutos"
            :to="{ name: 'modelos', query: { marca: marca._id } }"
            class="mc-enlace"
            :title="'Ver los modelos de ' + nombreDe(marca)"
            data-testid="marca-ver-modelos"
          >
            {{ cifra(marca.uso) }}<span class="mc-unidad"> {{ unidad(marca.uso, unidadUso) }}</span>
          </router-link>
          <template v-else-if="marca.uso">
            {{ cifra(marca.uso) }}<span class="mc-unidad"> {{ unidad(marca.uso, unidadUso) }}</span>
          </template>
          <span v-else class="cat-sin-dato"><span class="mc-guion" aria-hidden="true">—</span><span class="mc-sin">Sin {{ unidadUso.plural }}</span></span>
        </td>
        <td v-if="esAutos" class="mc-celda-conteo mc-celda-autos">
          <template v-if="marca.autos">
            {{ cifra(marca.autos) }}<span class="mc-unidad"> {{ unidad(marca.autos, UNIDAD_AUTOS) }}</span>
          </template>
          <span v-else class="cat-sin-dato"><span class="mc-guion" aria-hidden="true">—</span><span class="mc-sin">Sin autos</span></span>
        </td>
        <td class="cat-fila-acciones">
          <button
            type="button"
            class="cat-icono-btn"
            :aria-label="'Editar ' + nombreDe(marca)"
            data-testid="marca-editar"
            @click="editarMarca(marca)"
          >
            <AppIcon name="pencil" size="h-4 w-4" />
          </button>
          <button
            type="button"
            class="cat-icono-btn peligro"
            :aria-label="'Eliminar ' + nombreDe(marca)"
            data-testid="marca-eliminar"
            @click="pedirEliminar(marca)"
          >
            <AppIcon name="trash" size="h-4 w-4" />
          </button>
        </td>
      </tr>
    </template>

    <template #vacio>
      <AppIcon :name="busqueda.trim() ? 'search' : 'tag'" size="h-8 w-8" />
      <p v-if="busqueda.trim()">No hay marcas de {{ pestanaActual.nombre }} que coincidan con “{{ busqueda.trim() }}”.</p>
      <template v-else>
        <p>Aún no hay marcas de {{ pestanaActual.nombre }}.</p>
        <button type="button" class="rm-btn rm-btn-primario" @click="nuevaMarca">Registrar la primera</button>
      </template>
    </template>

    <BaseModal :visible="dialogoForm" :title="tituloForm" persistent @close="cerrarFormulario">
      <form novalidate data-testid="marca-form" @submit.prevent="guardarMarca">
        <AppField
          v-model="nombre"
          label="Nombre"
          required
          uppercase
          placeholder="TOYOTA"
          :error="intentoGuardar && !nombre.trim() ? 'Requerido.' : ''"
          testid="marca-nombre"
        />
        <div class="mc-vista-previa" aria-live="polite">
          <MarcaLogo :nombre="nombre" />
          <span>{{ nombre.trim() ? nombreDeMarca(nombre) : 'Así se verá en la lista' }}</span>
        </div>
      </form>
      <template #footer>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarFormulario">Cancelar</button>
        <button
          type="button"
          class="rm-btn rm-btn-primario"
          :disabled="!nombre.trim() || guardando"
          data-testid="marca-guardar"
          @click="guardarMarca"
        >
          {{ guardando ? 'Guardando…' : 'Guardar' }}
        </button>
      </template>
    </BaseModal>

    <BaseModal :visible="dialogoEliminar" title="Eliminar marca" @close="cerrarEliminar">
      <p class="cat-confirmacion">
        ¿Seguro de eliminar <b>{{ nombreDe(marcaSeleccionada) }}</b>? No se puede eliminar si tiene
        {{ unidadUso.plural }} registrados.
      </p>
      <template #footer>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarEliminar">Cancelar</button>
        <button
          type="button"
          class="rm-btn rm-btn-peligro"
          :disabled="guardando"
          data-testid="marca-confirmar-eliminar"
          @click="confirmarEliminar"
        >
          {{ guardando ? 'Eliminando…' : 'Eliminar' }}
        </button>
      </template>
    </BaseModal>
  </CatalogoPagina>
</template>

<script>
/*
 * Marcas — catálogo migrado sobre CatalogoPagina.
 *
 * Solo el nombre: la descripción nunca se usó y el tipo no se captura, lo
 * decide la pestaña (Autos → tipo 2, para modelos y vehículos; Refacciones →
 * tipo 1, para productos). Paginación, búsqueda, conteos y orden en el
 * backend (/api/brand/paginated). Autos: modelos y autos registrados, de
 * mayor a menor por autos; Refacciones: productos, de mayor a menor. Logo de
 * cada marca cuando lo tenemos (MarcaLogo); si no, sus iniciales. En Autos la
 * cifra de modelos lleva a Modelos filtrado por la marca.
 */
import ApiService from '@/services/apis'
import AppBusqueda from '@/components/ui/AppBusqueda.vue'
import AppField from '@/components/ui/AppField.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import BaseModal from '@/components/ui/BaseModal.vue'
import CatalogoPagina, { LIMITE_BASE } from '@/components/catalogo/CatalogoPagina.vue'
import MarcaLogo from '@/components/catalogo/MarcaLogo.vue'
import { nombreDeMarca } from '@/utils/formato'
import { paginaEquivalente } from '@/utils/paginacion'

export const TIPO_AUTO = 2
export const TIPO_REFACCION = 1

const UNIDAD_AUTOS = { singular: 'auto', plural: 'autos' }
// Lo más usado primero: autos registrados (Autos) o productos (Refacciones)
const ORDEN_INICIAL = { [TIPO_AUTO]: 'autos', [TIPO_REFACCION]: 'uso' }
const formatoCifra = new Intl.NumberFormat('es-MX')

const PESTANAS = [
  { tipo: TIPO_AUTO, clave: 'autos', label: 'Autos', nombre: 'autos', uso: { singular: 'modelo', plural: 'modelos' } },
  {
    tipo: TIPO_REFACCION,
    clave: 'refacciones',
    label: 'Refacciones',
    nombre: 'refacciones',
    uso: { singular: 'producto', plural: 'productos' },
  },
]

export default {
  name: 'marcas',
  components: { AppBusqueda, AppField, AppIcon, BaseModal, CatalogoPagina, MarcaLogo },
  data() {
    return {
      pestanas: PESTANAS,
      UNIDAD_AUTOS,
      tipo: TIPO_AUTO,
      marcas: [],
      totales: { autos: 0, refacciones: 0 },
      busqueda: '',
      ordenCampo: ORDEN_INICIAL[TIPO_AUTO],
      ordenAsc: false,
      paginaActual: 1,
      total: 0,
      totalPaginas: 1,
      limite: LIMITE_BASE,
      // Arranca cargando: CatalogoPagina mide el skeleton y emite `limite`
      cargando: true,
      yaCargo: false,
      errorCarga: false,
      guardando: false,
      dialogoForm: false,
      dialogoEliminar: false,
      marcaSeleccionada: null,
      nombre: '',
      intentoGuardar: false,
      aviso: '',
    }
  },
  computed: {
    pestanaActual() {
      return this.pestanas.find((p) => p.tipo === this.tipo)
    },
    unidadUso() {
      return this.pestanaActual.uso
    },
    esAutos() {
      return this.tipo === TIPO_AUTO
    },
    columnas() {
      if (this.esAutos) {
        return [
          { value: 'name', label: 'Marca' },
          { value: 'uso', label: 'Modelos', ancho: '110px', alinear: 'derecha' },
          { value: 'autos', label: 'Autos registrados', ancho: '170px', alinear: 'derecha' },
        ]
      }
      return [
        { value: 'name', label: 'Marca' },
        { value: 'uso', label: 'Productos', ancho: '130px', alinear: 'derecha' },
      ]
    },
    resumen() {
      return `${this.totales.autos + this.totales.refacciones} marcas registradas`
    },
    tituloForm() {
      if (this.marcaSeleccionada) return 'Editar marca'
      return this.tipo === TIPO_AUTO ? 'Nueva marca de auto' : 'Nueva marca de refacción'
    },
  },
  created() {
    // Contador no reactivo: identifica la consulta más reciente
    this.ultimaConsulta = 0
    this.limiteListo = false
  },
  beforeDestroy() {
    clearTimeout(this.avisoTimer)
  },
  methods: {
    nombreDeMarca,
    nombreDe(marca) {
      return marca ? nombreDeMarca(marca.name) : ''
    },
    cifra(n) {
      return formatoCifra.format(n)
    },
    unidad(n, { singular, plural }) {
      return n === 1 ? singular : plural
    },
    async initialize() {
      const consulta = ++this.ultimaConsulta
      this.cargando = true
      this.errorCarga = false
      try {
        const res = await ApiService.marcaPaginada({
          tipo: this.tipo,
          pagina: this.paginaActual,
          limite: this.limite,
          busqueda: this.busqueda.trim(),
          ordenarPor: this.ordenCampo,
          direccion: this.ordenAsc ? 'asc' : 'desc',
        })
        // Una respuesta que llega tarde (ya hay una consulta más nueva) se descarta
        if (consulta !== this.ultimaConsulta) return
        if (!Array.isArray(res?.marcas)) throw new Error('Respuesta inválida del servicio')
        this.marcas = res.marcas
        this.total = Number(res.total) || 0
        this.totalPaginas = Math.max(1, Number(res.totalPages) || 1)
        if (res.totales) this.totales = res.totales
        this.yaCargo = true
        if (this.paginaActual > this.totalPaginas) {
          this.paginaActual = this.totalPaginas
          return this.initialize()
        }
      } catch (err) {
        if (consulta !== this.ultimaConsulta) return
        this.errorCarga = true
        this.yaCargo = false
        this.marcas = []
        this.total = 0
      } finally {
        if (consulta === this.ultimaConsulta) this.cargando = false
      }
    },
    alCambiarLimite(nuevo) {
      if (!this.limiteListo) {
        this.limiteListo = true
        this.limite = nuevo
        return this.initialize()
      }
      if (nuevo === this.limite) return
      this.paginaActual = paginaEquivalente(this.paginaActual, this.limite, nuevo)
      this.limite = nuevo
      this.initialize()
    },
    cambiarTipo(tipo) {
      if (tipo === this.tipo) return
      this.tipo = tipo
      // Cada pestaña arranca con lo más usado primero
      this.ordenCampo = ORDEN_INICIAL[tipo]
      this.ordenAsc = false
      this.aplicarFiltros()
    },
    ordenarPor(campo) {
      if (campo === this.ordenCampo) {
        this.ordenAsc = !this.ordenAsc
      } else {
        this.ordenCampo = campo
        // El nombre empieza de la A; los conteos, del mayor
        this.ordenAsc = campo === 'name'
      }
      this.aplicarFiltros()
    },
    aplicarFiltros() {
      this.paginaActual = 1
      this.initialize()
    },
    cambiarPagina(pagina) {
      if (pagina < 1 || pagina > this.totalPaginas || pagina === this.paginaActual) return
      this.paginaActual = pagina
      this.initialize()
    },
    nuevaMarca() {
      this.marcaSeleccionada = null
      this.nombre = ''
      this.intentoGuardar = false
      this.dialogoForm = true
    },
    editarMarca(marca) {
      this.marcaSeleccionada = marca
      this.nombre = String(marca.name || '').trim()
      this.intentoGuardar = false
      this.dialogoForm = true
    },
    cerrarFormulario() {
      this.dialogoForm = false
      this.marcaSeleccionada = null
    },
    async guardarMarca() {
      this.intentoGuardar = true
      const nombre = this.nombre.trim().replace(/\s+/g, ' ').toUpperCase()
      if (!nombre || this.guardando) return
      const editando = Boolean(this.marcaSeleccionada)
      this.guardando = true
      try {
        // Solo nombre y tipo: la descripción que tenga la marca no se toca
        if (editando) {
          await ApiService.marcaEditar(this.marcaSeleccionada._id, { name: nombre, type: this.marcaSeleccionada.type })
        } else {
          await ApiService.marcaNuevo({ name: nombre, type: this.tipo })
        }
        this.cerrarFormulario()
        await this.initialize()
        this.mostrarAviso(editando ? 'Marca editada con éxito' : 'Marca creada con éxito')
      } catch (err) {
        // El servicio ya muestra el detalle (p. ej. "Marca ya está registrada")
      } finally {
        this.guardando = false
      }
    },
    pedirEliminar(marca) {
      this.marcaSeleccionada = marca
      this.dialogoEliminar = true
    },
    cerrarEliminar() {
      this.dialogoEliminar = false
      this.marcaSeleccionada = null
    },
    async confirmarEliminar() {
      this.guardando = true
      try {
        const res = await ApiService.borrarMarca(this.marcaSeleccionada._id)
        this.cerrarEliminar()
        if (this.marcas.length === 1 && this.paginaActual > 1) this.paginaActual--
        await this.initialize()
        this.mostrarAviso(res?.data?.message || 'Marca eliminada')
      } catch (err) {
        // El servicio ya muestra el motivo (p. ej. si tiene modelos o productos)
      } finally {
        this.guardando = false
      }
    },
    mostrarAviso(mensaje) {
      this.aviso = mensaje
      clearTimeout(this.avisoTimer)
      this.avisoTimer = setTimeout(() => {
        this.aviso = ''
      }, 4500)
    },
  },
}
</script>

<style scoped>
.mc-pestanas {
  display: inline-flex;
  gap: 2px;
  padding: 4px;
  border: 1px solid #e2e8ef;
  border-radius: 13px;
  background: #f8fafc;
}
.mc-pestana {
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #526273;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;
}
.mc-pestana:hover:not(.activa) {
  background: #edf3f8;
  color: #0b1b2e;
}
.mc-pestana.activa {
  background: #12304f;
  color: #fff;
  box-shadow: 0 2px 5px rgba(16, 42, 67, 0.17);
}
.mc-pestana:focus-visible {
  outline: 3px solid #7ab9f2;
  outline-offset: 2px;
}
.mc-cuenta {
  min-width: 22px;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(82, 98, 115, 0.12);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  text-align: center;
}
.mc-pestana.activa .mc-cuenta {
  background: rgba(255, 255, 255, 0.18);
}
.cat-pagina .cat-tabla td.mc-celda-conteo {
  color: #3d4b5f;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}
.mc-enlace {
  padding: 2px 4px;
  border-radius: 6px;
  color: #0a4f9c;
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: rgba(10, 79, 156, 0.3);
  text-underline-offset: 3px;
}
.mc-enlace:hover {
  background: #edf3f8;
  text-decoration-color: currentColor;
}
.mc-enlace:focus-visible {
  outline: 3px solid #7ab9f2;
  outline-offset: 1px;
}
.cat-pagina .cat-tabla td.mc-celda-autos {
  color: #0b1b2e;
  font-weight: 600;
}
/* En escritorio la unidad la dice el encabezado; en tarjetas (móvil) no hay */
.mc-unidad {
  display: none;
}
/* "Sin modelos" se lee en voz alta y en móvil; en escritorio basta el guion */
.mc-sin {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
.mc-vista-previa {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f3f5f8;
  color: #3d4b5f;
  font-size: 14px;
  font-weight: 600;
}
@media (max-width: 700px) {
  .cat-pagina .cat-tabla td.mc-celda-conteo {
    color: #526273;
    font-size: 12px;
    font-weight: 500;
    text-align: left;
  }
  .mc-unidad {
    display: inline;
  }
  .mc-guion {
    display: none;
  }
  .mc-sin {
    position: static;
    width: auto;
    height: auto;
    clip: auto;
  }
  /* Tarjeta: marca arriba y los dos conteos en una sola línea */
  .cat-pagina .cat-tabla tbody tr.mc-fila {
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
  }
  .cat-pagina .cat-tabla tr.mc-fila td.mc-celda-marca {
    grid-column: 1 / 3;
  }
  .cat-pagina .cat-tabla tr.mc-fila td.mc-celda-conteo {
    grid-row: 2;
    grid-column: 1;
  }
  .cat-pagina .cat-tabla tr.mc-fila td.mc-celda-autos {
    grid-column: 2;
  }
  .cat-pagina .cat-tabla tr.mc-fila td.mc-celda-autos::before {
    content: '·';
    margin: 0 6px;
    color: #9aa7b6;
    font-weight: 500;
  }
  .cat-pagina .cat-tabla tr.mc-fila td.cat-fila-acciones {
    grid-column: 3;
    grid-row: 1 / span 2;
  }
}
</style>
